/**
 * Address Helper Functions
 * Utilities for handling and validating address data, especially for protecting special characters in city names
 */

/**
 * Validates and normalizes city names while preserving hyphens and other valid characters
 * @param cityName - The city name to validate and normalize
 * @returns Normalized city name with preserved special characters
 */
export const normalizeCityName = (cityName: string): string => {
  if (!cityName || typeof cityName !== 'string') {
    return '';
  }

  // Trim whitespace from start and end
  let normalized = cityName.trim();
  
  // Remove multiple consecutive spaces and replace with single space
  normalized = normalized.replace(/\s+/g, ' ');
  
  // Remove multiple consecutive hyphens and replace with single hyphen
  normalized = normalized.replace(/-+/g, '-');
  
  // Remove hyphens at the start or end of the string
  normalized = normalized.replace(/^-+|-+$/g, '');
  
  return normalized;
};

/**
 * Validates that a city name contains only allowed characters
 * Allowed: Letters (including German umlauts), spaces, hyphens, apostrophes, dots
 * @param cityName - The city name to validate
 * @returns True if valid, false otherwise
 */
export const isValidCityName = (cityName: string): boolean => {
  if (!cityName || typeof cityName !== 'string') {
    return false;
  }

  // Must contain at least 2 characters and only allowed characters
  const cityRegex = /^[a-zA-ZäöüÄÖÜß\s\-'.]{2,}$/;
  return cityRegex.test(cityName.trim());
};

/**
 * Ensures city names with hyphens are properly preserved for database storage
 * This function explicitly protects common German city names with hyphens
 * @param cityName - The city name to protect
 * @returns Protected city name with preserved formatting
 */
export const protectCityNameForDatabase = (cityName: string): string => {
  if (!cityName || typeof cityName !== 'string') {
    return '';
  }

  // Normalize the city name first
  const normalized = normalizeCityName(cityName);
  
  // List of common German city name patterns that should preserve hyphens
  const protectedPatterns = [
    /Sankt-\w+/gi,           // Sankt-Augustin, Sankt-Georgen, etc.
    /Bad-\w+/gi,             // Bad-Homburg, etc. 
    /Neu-\w+/gi,             // Neu-Ulm, etc.
    /Alt-\w+/gi,             // Alt-Hohenschönhausen, etc.
    /Klein-\w+/gi,           // Klein-Winternheim, etc.
    /Groß-\w+/gi,            // Groß-Gerau, etc.
    /Unter-\w+/gi,           // Unter-Flockenbach, etc.
    /Ober-\w+/gi,            // Ober-Ramstadt, etc.
    /\w+-am-\w+/gi,          // Frankfurt-am-Main, etc.
    /\w+-an-der-\w+/gi,      // Frankfurt-an-der-Oder, etc.
    /\w+-auf-\w+/gi,         // Rüdesheim-auf-Rügen, etc.
  ];

  // Check if the city name matches any protected pattern
  const isProtectedPattern = protectedPatterns.some(pattern => pattern.test(normalized));
  
  if (isProtectedPattern) {
    // Add a comment to the database field to indicate this should preserve hyphens
    console.log(`Protected city name with hyphens: ${normalized}`);
  }
  
  return normalized;
};

/**
 * Common German cities with hyphens that should be preserved
 */
export const COMMON_HYPHENATED_CITIES = [
  'Sankt-Augustin',
  'Sankt-Georgen', 
  'Sankt-Ingbert',
  'Sankt-Leon-Rot',
  'Bad-Homburg',
  'Bad-Kreuznach',
  'Bad-Nauheim',
  'Bad-Oeynhausen',
  'Neu-Ulm',
  'Neu-Isenburg',
  'Klein-Winternheim',
  'Groß-Gerau',
  'Groß-Umstadt',
  'Frankfurt-am-Main',
  'Frankfurt-an-der-Oder',
  'Worms-Herrnsheim',
  'Halle-Neustadt',
  'Leverkusen-Opladen',
  'Bergisch-Gladbach',
  'Garmisch-Partenkirchen',
];

/**
 * Validates if a city name is in the list of known hyphenated cities
 * @param cityName - The city name to check
 * @returns True if it's a known hyphenated city
 */
export const isKnownHyphenatedCity = (cityName: string): boolean => {
  if (!cityName) return false;
  
  const normalized = cityName.trim().toLowerCase();
  return COMMON_HYPHENATED_CITIES.some(city => 
    city.toLowerCase() === normalized
  );
};

